import classNames from "classnames";
import { DeleteDialog } from "components";
import { PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";
import TravelClassDropdown from "components/dropdowns/reactselect/TravelClassDropdown";
import { InPageEditingProvider } from "components/MainSectionTemplate/InPageEditingContext";
import { ContactSidePanel } from "components/SidePanel/contact/ContactSidePanel";
import { SidePanelContext } from "components/SidePanel/SidePanelContext";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { useValidators } from "formvalidation/hooks";
import React, { useContext, useEffect } from "react";
import { getFlightTravelClass } from "utilities/enumutils";
import { PassengerCardField } from "./PassengerCardField";
import FileList from "components/FileUpload/filelist";
import { ManageFlightPassengerFilesModal } from "components/FileUpload/ManageMultipleFiles/ManageFlightPassengerFilesModal";
import { emptySubmitChangeFunc } from "components/MainSectionTemplate/emptySubmitChangeFunc";

interface Props {
  isInSidePanel: boolean
  isPrivateJet: boolean
  passenger: server.dto.FlightPassenger
  onDeleted: () => Promise<void>
  isInPageEditable: boolean
}

interface PassengerState {
  contact: server.dto.ContactSmallV2
  travelClass: server.dto.FlightTravelClass
  seat: string
  bookingCode: string
  meal: string
  eTicket: string
  baggage: string

  files: server.dto.File[]
}

export const PassengerCard = (props: Props) => {
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
  const [state, setState] = React.useState<PassengerState>({
    contact: props.passenger.contact,
    travelClass: getFlightTravelClass(props.passenger.travelClass),
    seat: props.passenger.seat,
    bookingCode: props.passenger.bookingReference,
    meal: props.passenger.meal,
    eTicket: props.passenger.ticketNumber,
    baggage: props.passenger.baggage,
    files: props.passenger.files,
  });

  useEffect(() => {
    setState({
      contact: props.passenger.contact,
      travelClass: getFlightTravelClass(props.passenger.travelClass),
      seat: props.passenger.seat,
      bookingCode: props.passenger.bookingReference,
      meal: props.passenger.meal,
      eTicket: props.passenger.ticketNumber,
      baggage: props.passenger.baggage,

      files: props.passenger.files,
    });
  }, [props.passenger]);

  const sidePanelContext = useContext(SidePanelContext);
  const isNarrow = props.isInSidePanel && sidePanelContext.layoutType === "narrow";
  const validators = useValidators();

  const onValueChanged = (key: string, value: any) => {
    if (key === "contact") {
      setState(prev => ({ ...prev, contact: value }));
    } else if (key === "travelClass") {
      setState(prev => ({ ...prev, travelClass: value }));
    } else if (key === "seat") {
      setState(prev => ({ ...prev, seat: value }));
    } else if (key === "bookingCode") {
      setState(prev => ({ ...prev, bookingCode: value }));
    } else if (key === "meal") {
      setState(prev => ({ ...prev, meal: value }));
    } else if (key === "eTicket") {
      setState(prev => ({ ...prev, eTicket: value }));
    } else if (key === "baggage") {
      setState(prev => ({ ...prev, baggage: value }));
    }
  }

  const onDelete = async () => {
    await window.CoreApi.Flight.DeletePassenger({ flightPassengerId: props.passenger.flightPassengerId });
    await props.onDeleted();
  };

  const refetchFiles = async () => {
    const response = await window.CoreApi.Flight.GetPassenger({ flightPassengerId: props.passenger.flightPassengerId });

    if (response.data.hasErrors) {
      return;
    }

    setState(prev => ({ ...prev, files: response.data.data.files }));
  }

  const getUpdateInstruction = (ps: PassengerState): server.dto.UpdateFlightPassengerInstruction => ({
    baggage: ps.baggage,
    bookingReference: ps.bookingCode,
    flightPassengerId: props.passenger.flightPassengerId,
    meal: ps.meal,
    seat: ps.seat,
    ticketNumber: ps.eTicket,
    travelClass: ps.travelClass ? ps.travelClass.value : "unknown",
  });

  return <InPageEditingProvider
    enabled={props.isInPageEditable}
    validate={validators.validateAll}
    onValueChanged={onValueChanged}
  >
    <div className={classNames("passenger-card-grid", {
      "passenger-card-grid-side-panel-narrow": isNarrow,
      "passenger-card-grid-private-jet": props.isPrivateJet,
    })}>
      <div className="flex gap justify-space-between" style={{ gridArea: "passenger" }}>
        <PassengerCardField
          gridArea=""
          field={{
            key: "contact",
            label: "",
            render: () => <ContactSidePanel
              contactId={state.contact.contactId}
              trigger={<a>{state.contact.name.fullName}</a>}
            />,
          }}
        />
        {(props.isInPageEditable && isNarrow) && <PhosphorTrashButton
          layout="in-square"
          onClick={() => setIsDeleting(true)}
        />}
      </div>
      {!props.isPrivateJet && <PassengerCardField
        gridArea="travelClass"
        field={{
          key: "travelClass",
          label: RESX.Flight.TravelClass,
          isEmpty: !state.travelClass || state.travelClass.value === "unknown",
          render: () => <>{state.travelClass.text}</>,
          inPageEdit: {
            render: ({ value, onChange }) => <TravelClassDropdown
              value={value}
              onChange={v => onChange(v, true)}
              config={{
                focusOnMount: true,
                openMenuOnFocus: true
              }}
            />,
            type: "custom",
            fullWidth: true,
            initialValue: state.travelClass,
            submitChange: (travelClass: server.dto.FlightTravelClass) => {
              return window.CoreApi.Flight.UpdatePassenger(
                getUpdateInstruction({ ...state, travelClass })
              )
            }
          }
        }}
      />}
      {!props.isPrivateJet && <PassengerCardField
        gridArea="seat"
        field={{
          key: "seat",
          isEmpty: !state.seat,
          label: RESX.Flight.Seats,
          render: () => <>{state.seat}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxSeatLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.seat,
            submitChange: (seat: string) => {
              return window.CoreApi.Flight.UpdatePassenger(
                getUpdateInstruction({ ...state, seat })
              )
            }
          }
        }}
      />}
      {!props.isPrivateJet && <PassengerCardField
        gridArea="bookingCode"
        field={{
          key: "bookingCode",
          isEmpty: !state.bookingCode,
          label: RESX.Flight.BookingCode,
          render: () => <>{state.bookingCode}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              maxLength={serverReference.constants.flight.maxBookingReferenceLength}
              focusOnMount
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.bookingCode,
            submitChange: (bookingCode: string) => {
              return window.CoreApi.Flight.UpdatePassenger(
                getUpdateInstruction({ ...state, bookingCode })
              )
            }
          }
        }}
      />}
      <PassengerCardField
        gridArea="meal"
        field={{
          key: "meal",
          isEmpty: !state.meal,
          label: RESX.Flight.Meal,
          render: () => <>{state.meal}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxMealLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.meal,
            submitChange: (meal: string) => {
              return window.CoreApi.Flight.UpdatePassenger(
                getUpdateInstruction({ ...state, meal })
              )
            }
          }
        }}
      />
      {!props.isPrivateJet && <PassengerCardField
        gridArea="eTicket"
        field={{
          key: "eTicket",
          isEmpty: !state.eTicket,
          label: RESX.Flight.ETicket,
          render: () => <>{state.eTicket}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxTicketNumberLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.eTicket,
            submitChange: (eTicket: string) => {
              return window.CoreApi.Flight.UpdatePassenger(
                getUpdateInstruction({ ...state, eTicket })
              )
            }
          }
        }}
      />}
      <PassengerCardField
        gridArea="baggage"
        field={{
          key: "baggage",
          isEmpty: !state.baggage,
          label: RESX.Flight.Baggage,
          render: () => <OptionalTextAreaDetails>{state.baggage}</OptionalTextAreaDetails>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextAreaInput
              value={value}
              rows={2}
              onChange={v => onChange(v)}
              onCtrlEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxBaggageLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.baggage,
            submitChange: (baggage: string) => {
              return window.CoreApi.Flight.UpdatePassenger(
                getUpdateInstruction({ ...state, baggage })
              )
            }
          }
        }}
      />
      <PassengerCardField
        gridArea="files"
        field={{
          key: "files",
          isEmpty: !state.files || state.files.length === 0,
          label: RESX.File.Files,
          render: () => <FileList editDisabled files={state.files} onAddFilesClick={() => { }} />,
          inPageEdit: {
            render: ({ cancel }) => <ManageFlightPassengerFilesModal
              flightPassengerId={props.passenger.flightPassengerId}
              isOpen
              onClose={cancel}
              canEdit={site.permissions.flight.canCreate}
              files={state.files}
              onFilesChanged={async () => {
                await refetchFiles();
              }}
            />,
            type: "custom",
            fullWidth: true,
            initialValue: state.files,
            submitChange: () => emptySubmitChangeFunc(),
          }
        }}
      />
      {(props.isInPageEditable && !isNarrow) && <div style={{ gridArea: "buttonDelete" }}>
        <PhosphorTrashButton
          layout="in-square"
          onClick={() => setIsDeleting(true)}
        />
      </div>}
      <DeleteDialog
        spinForever
        isOpen={isDeleting}
        onDelete={onDelete}
        handleClose={() => { setIsDeleting(false); }}
        message={RESX.GeneralWarnings.DeleteSure}
      />
    </div>
  </InPageEditingProvider>
}
